/* eslint-disable max-lines-per-function */
import React, { useState, useRef, useEffect } from 'react';
import { DeliveryUtilizationReportProvider, useDeliveryUtilizationReport } from './DeliveryUtilizationReportProvider';
import DeliveryUtilizationReportTable from './DeliveryUtilizationReportTable';
import { Box, Popper, ClickAwayListener } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);
import './DeliveryUtilizationReport.scss';
import PropTypes from 'prop-types';
import {ReactComponent as ErrorInfoIcon } from '../../../Assets/images/svg/errinfo.svg';
import DataGridComponent from '../../../Components/DataGrid/dataGrid';

const Loader = () => {
  return (
    <div className="delivery-utilization-loader">
      <div>Loading...</div>
    </div>
  );
};

const DeliveryUtilizationReportHeader = ({
  fromDate,
  toDate,
  onFromDateClick,
  onToDateClick,
  onApplyClick,
  fromRef,
  toRef,
  onErrorDismiss,
  error,
}) => (
  <Box
    sx={{
      background: 'white',
      padding: '12px 24px',
      borderRadius: '8px',
      margin: '8px 16px',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'center', sm: 'center' },
      justifyContent: { xs: 'center', sm: 'space-between' },
      flexWrap: 'wrap',
      gap: { xs: 2, sm: 0 },
      textAlign: { xs: 'center', sm: 'left' },
    }}
  >
    <Box
      component="span"
      sx={{
        fontWeight: 600,
        fontSize: '16px',
        fontFamily: 'Mukta, sans-serif',
        color: '#000',
        width: { xs: '100%', sm: 'auto' },
      }}
    >
      Delivery Utilization Report
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'center', sm: 'flex-end' },
      }}
    >
      {error && (
        <ClickAwayListener onClickAway={onErrorDismiss}>
          <Box className="warning-toast-over-dates">
            <Box className="warning-content" sx={{ display: 'flex', alignItems: 'center' }}>
              <ErrorInfoIcon className="warning-icon" />
              <Box component="span" className="warning-text">
                {error}
              </Box>
            </Box>
          </Box>
        </ClickAwayListener>
      )}
      <button
        ref={fromRef}
        type="button"
        className="from-date-field"
        style={{
          minWidth: 90,
          textAlign: 'center',
          border: 'none',
          borderBottom: '1px solid #ccc',
          background: '#fff',
          padding: '2px 8px',
          cursor: 'pointer',
          color: '#49454E',
          fontSize: '14px',
          fontFamily: 'Mukta, sans-serif',
          position: 'relative',
        }}
        onClick={onFromDateClick}
        title="Select from date"
        aria-label="Select from date"
      >
        <span>From </span>
        {fromDate ? dayjs(fromDate).format('DD/MM/YYYY') : '--/--/----'}
      </button>

      <Box
        component="span"
        sx={{
          color: '#49454E',
          fontSize: '14px',
          fontFamily: 'Mukta, sans-serif',
          display: { xs: 'none', sm: 'inline' },
        }}
      >
        –
      </Box>

      <button
        ref={toRef}
        type="button"
        className="to-date-field"
        style={{
          minWidth: 90,
          textAlign: 'center',
          border: 'none',
          borderBottom: '1px solid #ccc',
          background: '#fff',
          padding: '2px 8px',
          cursor: 'pointer',
          color: '#49454E',
          fontSize: '14px',
          fontFamily: 'Mukta, sans-serif',
          position: 'relative',
        }}
        onClick={onToDateClick}
        title="Select to date"
        aria-label="Select to date"
      >
        <span>To </span>
        {toDate ? dayjs(toDate).format('DD/MM/YYYY') : '--/--/----'}
      </button>

      <Box
        sx={{
          width: { xs: '100%', sm: 'auto' },
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-end' },
        }}
      >
        <button
          type="button"
          onClick={onApplyClick}
          title="Apply date filter"
          aria-label="Apply date filter"
          style={{
            backgroundColor: '#7BC9FF',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            minWidth: 60,
            height: 24,
            fontSize: '14px',
            fontFamily: 'Mukta, sans-serif',
          }}
        >
          Apply
        </button>
      </Box>
    </Box>
  </Box>

);

DeliveryUtilizationReportHeader.propTypes = {
  fromDate: PropTypes.object,
  toDate: PropTypes.object,
  onFromDateClick: PropTypes.func.isRequired,
  onToDateClick: PropTypes.func.isRequired,
  onApplyClick: PropTypes.func.isRequired,
  fromRef: PropTypes.object.isRequired,
  toRef: PropTypes.object.isRequired,
  onErrorDismiss: PropTypes.func.isRequired,
  error: PropTypes.string,
};

const DeliveryUtilizationReportContent = () => {
  const {
    fromdate,
    todate,
    setFromDate,
    setToDate,
    setLoading,
    summaryDetails,
    triggerRefresh,
    loading,
  } = useDeliveryUtilizationReport();
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(null);
  const [summaryData, setSummaryData] = useState({
    utilizationColumns: [],
    utilizationTableData: [],
  });
  const fromRef = useRef(null);
  const toRef = useRef(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleApplyClick = () => {
    if (fromdate && todate) {
      const isValid = dayjs(todate).isSameOrBefore(dayjs(fromdate).add(1, 'year'));

      if (!isValid) {
        setError('Date range has to be within 12 months');
        return;
      }
      setLoading(true);
      triggerRefresh();
    }
  };


  const handleErrorDismiss = () => {
    setError('');
  };

  useEffect(() => {
    setSummaryData({
      utilizationColumns: summaryDetails?.utilizationColumns || [],
      utilizationTableData: summaryDetails?.utilizationTableData || [],
    });
  }, [summaryDetails]);


  const validateAndSetFromDate = (date) => {
    setFromDate(date);
    setPicker(null);
  };
  const validateAndSetToDate = (date) => {
    setToDate(date);
    setPicker(null);
  };

  const getDynamicSummaryCols = (cols) => {
    const lastColIndex = (cols || []).length - 1;
    return (cols || []).map((col, index) => ({
      ...col,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: index === lastColIndex ? 'no-right-border' : '',
      cellClassName: index === lastColIndex ? 'no-right-border' : '',
    }));
  };

  const updatedColumns = getDynamicSummaryCols(summaryData.utilizationColumns);

  const getDynamicSummaryData = (rows) => {
    return (rows || []).map((row, index) => ({
      ...row,
      id: `${row.vertical}-${index}`,
    }));
  };

  const updatedRows = getDynamicSummaryData(summaryData.utilizationTableData);

  const calendarStyles = {
    bgcolor: '#fff',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '320px',
    minWidth: '260px',
    fontFamily: 'Mukta, sans-serif',
    overflow: 'hidden',
    '& .MuiPickersLayout-root': {
      maxHeight: '350px',
    },
    '& .MuiPickersLayout-contentWrapper': {
      overflowY: 'auto',
      maxHeight: '320px',
    },
    '& .MuiYearCalendar-root': {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)', // Responsive year grid
      gap: '8px',
      padding: '16px',
    },
    '& .MuiPickersYear-yearButton': {
      fontFamily: 'Mukta, sans-serif',
      fontSize: '14px',
      height: '36px',
      width: '100%',
    },
    '& .MuiTypography-root': {
      fontFamily: 'Mukta, sans-serif',
    },
    '& .MuiPickersCalendarHeader-label': {
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Mukta, sans-serif',
    },
    '& .MuiPickersDay-root': {
      fontSize: '12px',
      fontFamily: 'Mukta, sans-serif',
    },
    '& .MuiDayCalendar-weekDayLabel': {
      fontSize: '12px',
      fontWeight: '600',
      fontFamily: 'Mukta, sans-serif',
    },
    '& .MuiPickersArrowSwitcher-button': {
      fontFamily: 'Mukta, sans-serif',
    },
    boxShadow:
      '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
  };


  return (
    <div className="delivery-utilization-report__outer-layer">
      <div className="delivery-utilization-report__layout">
        <DeliveryUtilizationReportHeader
          fromDate={fromdate}
          toDate={todate}
          onFromDateClick={() => setPicker('from')}
          onToDateClick={() => setPicker('to')}
          onApplyClick={handleApplyClick}
          fromRef={fromRef}
          toRef={toRef}
          error={error}
          onErrorDismiss={handleErrorDismiss}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Popper
            open={picker === 'from'}
            anchorEl={fromRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setPicker(null)}>
              <Box sx={{ padding: 1, maxWidth: '100vw', overflowX: 'auto' }}>
                <DateCalendar
                  value={fromdate}
                  onChange={validateAndSetFromDate}
                  maxDate={todate}
                  sx={calendarStyles}
                />
              </Box>
            </ClickAwayListener>
          </Popper>

          <Popper
            open={picker === 'to'}
            anchorEl={toRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={() => setPicker(null)}>
              <Box sx={{ padding: 1, maxWidth: '100vw', overflowX: 'auto' }}>
                <DateCalendar
                  value={todate}
                  onChange={validateAndSetToDate}
                  minDate={fromdate}
                  sx={calendarStyles}
                />
              </Box>
            </ClickAwayListener>
          </Popper>

        </LocalizationProvider>

        <div className="du-data-grid">
          {
            loading ? <Loader /> : <DataGridComponent
              dataRows={updatedRows}
              dataColumns={updatedColumns}
              pageSize={10}
              isFilteredRows={[]}
              sortFilterColumns={[]}
              isEdit={false}
              gridCheckbox={false}
              hideFooter={true}
              hideAutoheight={false}
              hideSeparator={true}
            />
          }
        </div>

        <div className="table-utility">
          <DeliveryUtilizationReportTable />
        </div>
      </div>
    </div>
  );
};

const DeliveryUtilizationReport = () => (
  <DeliveryUtilizationReportProvider>
    <DeliveryUtilizationReportContent />
  </DeliveryUtilizationReportProvider>
);

export default DeliveryUtilizationReport;
